import React, {useContext, useState} from 'react';
import {useMediaQuery} from "react-responsive";
import { GiSave, GiCancel } from "react-icons/gi";
import { BsPencilSquare } from "react-icons/bs";
import { Bounce, toast } from "react-toastify";

import { DisplayNameRequest } from "../goveeenergymonitoringpb/internal/proto/goveeenergymonitoring_pb"
import ExpandedSmartPlugChartDialog from "./ExpandedSmartPlugChartDialog";
import {AuthContext} from "../contexts/AuthContext";

import WifiIndicator, {
  DBMToSignalStrength,
  SignalStrength
} from 'react-wifi-indicator';
import moment from "moment/moment";
import {StatusCode} from "grpc-web";

const signalStrengthOffset = 15;

export const CalculateAverageDailykWhPerDay = (lastItem) => {
  if (!lastItem) {
    return 0.0;
  }
  const timePoweredOnSeconds = lastItem.getTimePoweredOnSeconds();
  const accumulatedWatts = lastItem.getAccumulatedPowerDeciWatts() / 10.0;
  return (accumulatedWatts / 1000.0) / (timePoweredOnSeconds / 86400.0);
}

export const CalculateAverageMonthlyCost = (lastItem) => {
  if (!lastItem) {
    return 0.0;
  }
  const killwattHoursPerDay = CalculateAverageDailykWhPerDay(lastItem);
  const dollarsPerDay = killwattHoursPerDay * 0.21;
  return dollarsPerDay * 30;
}

export const CalculateCentsCost = (thisItem, previousItem) => {
  if (!previousItem || !thisItem) {
    return null;
  }

  if (thisItem.getTimePoweredOnSeconds() <= previousItem.getTimePoweredOnSeconds()) {
    return null;
  }

  const elapsedSeconds = thisItem.getTimePoweredOnSeconds() - previousItem.getTimePoweredOnSeconds();
  const kilowattsDelta = (thisItem.getAccumulatedPowerDeciWatts() - previousItem.getAccumulatedPowerDeciWatts()) / 10.0 / 1000;

  // portion of hour * kilowatts * $0.21/kwh
  const kilowatts = kilowattsDelta * (3600/elapsedSeconds);
  const cents = kilowatts * 21;

  // console.log("elapsedSeconds", elapsedSeconds, "elapsedSecondsTime", elapsedSecondsTime, "kilowattsDelta", kilowattsDelta, "milliwatts", kilowatts, "cents", cents, "portionOfHour", elapsedSeconds/3600);
  return cents;
}

export const CalculateBucketIntervalMillis = (since, until) => {
  const elapsedHours = (until - since) / 3600000;
  if (elapsedHours < 2) {
    return -1;
  }
  const elapsedDays = (until - since) / (24*3600*1000);

  return elapsedDays >= 60
      ? 7*24*3600*1000 // weekly
      : elapsedDays >= 5
          ? 24*3600*1000 // daily
          : 3600000; // hourly
}

export const GetBucketedMetadata = (bucketedSamples, since, until) => {
  const bucketIntervalMillis = CalculateBucketIntervalMillis(since, until);
  if (bucketedSamples && bucketIntervalMillis > 0) {
    if (bucketedSamples.getHourlySummarySamples() && bucketedSamples.getHourlySummarySamples().getSummarySamplesList().length > 0 && bucketIntervalMillis === 3600*1000) {
      // hourly
      return bucketedSamples.getHourlySummarySamples();
    } else if (bucketedSamples.getDailySummarySamples() && bucketedSamples.getDailySummarySamples().getSummarySamplesList().length > 0 && bucketIntervalMillis === 24*3600*1000) {
      // daily
      return bucketedSamples.getDailySummarySamples();
    } else if (bucketedSamples.getWeeklySummarySamples() && bucketedSamples.getWeeklySummarySamples().getSummarySamplesList().length > 0 && bucketIntervalMillis === 7*24*3600*1000) {
      // weekly
      return bucketedSamples.getWeeklySummarySamples();
    } else if (bucketedSamples.getMonthlySummarySamples() && bucketedSamples.getMonthlySummarySamples().getSummarySamplesList().length > 0 && bucketIntervalMillis === 4*7*24*3600*1000) {
      // monthly
      return bucketedSamples.getMonthlySummarySamples();
    }
  }
  return null;
}

export const CalculateBucketedData = (bucketedSamples, since, until) => {
  var formattedBucketedData = [];
  const bucketIntervalMillis = CalculateBucketIntervalMillis(since, until);
  if (bucketedSamples && bucketIntervalMillis > 0) {
    if (bucketedSamples.getHourlySummarySamples() && bucketedSamples.getHourlySummarySamples().getSummarySamplesList().length > 0 && bucketIntervalMillis === 3600*1000) {
      // hourly
      formattedBucketedData = bucketedSamples.getHourlySummarySamples().getSummarySamplesList().map((o) => ({
        "timestamp": o.getTime(),
        "totalwatts": o.getTotalWatts() / 1000,
        "totalcents": o.getTotalCents(),
      })) || [];
    } else if (bucketedSamples.getDailySummarySamples() && bucketedSamples.getDailySummarySamples().getSummarySamplesList().length > 0 && bucketIntervalMillis === 24*3600*1000) {
      // daily
      formattedBucketedData = bucketedSamples.getDailySummarySamples().getSummarySamplesList().map((o) => ({
        "timestamp": o.getTime(),
        "totalwatts": o.getTotalWatts() / 1000,
        "totalcents": o.getTotalCents(),
      })) || [];
    } else if (bucketedSamples.getWeeklySummarySamples() && bucketedSamples.getWeeklySummarySamples().getSummarySamplesList().length > 0 && bucketIntervalMillis === 7*24*3600*1000) {
      // weekly
      formattedBucketedData = bucketedSamples.getWeeklySummarySamples().getSummarySamplesList().map((o) => ({
        "timestamp": o.getTime(),
        "totalwatts": o.getTotalWatts() / 1000,
        "totalcents": o.getTotalCents(),
      })) || [];
    } else if (bucketedSamples.getMonthlySummarySamples() && bucketedSamples.getMonthlySummarySamples().getSummarySamplesList().length > 0 && bucketIntervalMillis === 4*7*24*3600*1000) {
      // monthly
      formattedBucketedData = bucketedSamples.getMonthlySummarySamples().getSummarySamplesList().map((o) => ({
        "timestamp": o.getTime(),
        "totalwatts": o.getTotalWatts() / 1000,
        "totalcents": o.getTotalCents(),
      })) || [];
    }
  }
  return formattedBucketedData;
}

export const CalculateHourlyData = (data, since, until) => {
  if (!data || data.length < 2) {
    return [];
  }

  const bucketIntervalMillis = CalculateBucketIntervalMillis(since, until);
  if (bucketIntervalMillis <= 0) {
    return [];
  }

  let samples = [];
  let start = data[0];
  let startBucketTime = start.getTime() - (start.getTime() % bucketIntervalMillis);
  for (let i = 1; i < data.length; i++) {
    let current = data[i];
    let bucketTime = current.getTime() - (current.getTime() % bucketIntervalMillis);
    if (startBucketTime === bucketTime) {
      continue;
    }
    if (current.getTimePoweredOnSeconds() <= start.getTimePoweredOnSeconds()) {
      // counter reset
      samples.push({
        "timestamp": startBucketTime,
        "hourlykwh": null,
        "hourlydollars": null,
      });
    } else {
      const currentElapsedHours = (current.getTimePoweredOnSeconds() - start.getTimePoweredOnSeconds()) / 3600;
      const kilowattsDelta = (current.getAccumulatedPowerDeciWatts() - start.getAccumulatedPowerDeciWatts()) / 10.0 / 1000;

      // portion of hour * kilowatts * $0.21/kwh
      const kilowatts = ((bucketIntervalMillis/3600000)/currentElapsedHours) * kilowattsDelta;
      const dollars = kilowatts * 21;

      // console.log("Bucket change: bucketTime", new Date(startBucketTime), "secondsStart", start.getTimePoweredOnSeconds(), "secondsEnd", current.getTimePoweredOnSeconds(),
      //     "currentElapsedHours", currentElapsedHours, "kilowattsDelta", kilowattsDelta,
      //     "bucketIntervalMillis", bucketIntervalMillis/3600000,
      //     "kilowatts", kilowatts, "dollars", dollars);

      samples.push({
        "timestamp": startBucketTime,
        "hourlykwh": kilowatts,
        "hourlydollars": dollars,
      });
    }

    start = current;
    startBucketTime = bucketTime;
  }

  // One more final entry 
  let current = data[data.length - 1];
  if (start.getTime() !== current.getTime()) {
    if (current.getTimePoweredOnSeconds() > start.getTimePoweredOnSeconds()) {
      const currentElapsedHours = (current.getTimePoweredOnSeconds()
          - start.getTimePoweredOnSeconds()) / 3600;
      const kilowattsDelta = (current.getAccumulatedPowerDeciWatts()
          - start.getAccumulatedPowerDeciWatts()) / 10.0 / 1000;

      // portion of hour * kilowatts * $0.21/kwh
      const kilowatts = (bucketIntervalMillis/3600000)/currentElapsedHours * kilowattsDelta;
      const dollars = kilowatts * 21;

      samples.push({
        "timestamp": startBucketTime,
        "hourlykwh": kilowatts,
        "hourlydollars": dollars,
      });
    }
  }

  return samples;
}

const SmartPlugHeaderLabels = ({client, hasData, lastItem, avgDollarsPerMonth, device, onLegendClick}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [displayName, setDisplayName] = useState(device.getDisplayName());
  const { fetchUser, getAccessToken } = useContext(AuthContext);

  const onLabelClick = (e) => {
    setIsEditing(!isEditing);
    setDisplayName(device.getDisplayName());
  };

  const updateDisplayName = (isLastTry) => {
    const accessToken = getAccessToken();
    const metadata = {"Authorization": "Bearer " + accessToken}
    if (!accessToken) {
      return;
    }

    const request = new DisplayNameRequest();
    request.setSmartPlugId(device.getId());
    request.setDisplayName(displayName);
    console.log("Updating displayName of device", device.getId(), "to", displayName);
    client.updateSmartPlugDisplayName(request, metadata, (err, response) => {
      if (err) {
        if (err.code === StatusCode.UNAUTHENTICATED) {
          if (!isLastTry) {
            console.error("Received auth error updating display name, attempting to refresh auth and retry", err)
            fetchUser().then(function () {
              console.log("Refresh attempt complete, retrying update display name")
              updateDisplayName(true);
            });
            return;
          }
        }

        console.error("Device update failed", err, response);
        toast.error('An error occurred updating display name: "' + err.message + '"', {
          position: "bottom-left",
          autoClose: 4000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          theme: "light",
          transition: Bounce,
        });
      } else {
        setDisplayName(displayName)
        device.setDisplayName(displayName);
        toast.info('Display name updated', {
          position: "bottom-left",
          autoClose: 4000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          theme: "light",
          transition: Bounce,
        });
      }
      setIsEditing(!isEditing);
    });
  }

  const handleSubmit = (e) => {
    // prevents the default browser behavior of submitting the form and refreshing the page.
    e.preventDefault();

    updateDisplayName();
  };

  let deviceDisplayName;
  if (isEditing) {
    deviceDisplayName =
        <div className="device-header-labels-display-name">
          <input type="text"
                 value={displayName}
                 onChange={(e) => setDisplayName(e.target.value)}
                 maxLength={100}
                 placeholder="Name"/>
          <GiSave className="device-header-labels-display-name-form" title="Update device display name" size="18px" onClick={handleSubmit}/>
          <GiCancel className="device-header-labels-display-name-form" title="Cancel" size="18px" onClick={onLabelClick}/>
        </div>
  } else {
    deviceDisplayName =
        <h3 className="device-header-labels-name"
            title={device.getId()}>
          <a onClick={onLabelClick}>{device.getDisplayName()}</a>
          <BsPencilSquare className="device-header-labels-display-name-edit" title="Edit device display name" size="14px" onClick={onLabelClick}/>
      </h3>
  }

  return (
      <div className="device-header-labels-container">
        <div className="device-header-labels">
          {deviceDisplayName}
          {hasData &&
              <>
                <a onClick={onLegendClick} name="temperature-legend" className="device-header-labels-byline device-header-labels-watts">
                  Power: {(Math.round(lastItem.getCentiWatts() / 100.0 * 10)
                    / 10).toFixed(1)} W,
                </a>
                <a onClick={onLegendClick} name="humidity-legend" className="device-header-labels-byline device-header-labels-amps">
                  Amps: {(Math.round(lastItem.getCentiAmps() / 100.0 * 100)
                    / 100).toFixed(2)} A,
                </a>
               <a onClick={onLegendClick} name="dollars-legend" className="device-header-labels-byline device-header-labels-dollars">
                  Cost: ${(Math.round(avgDollarsPerMonth * 100)
                    / 100).toFixed(2)}/mo
                </a>
              </>
          }
        </div>
      </div>
  );
};

const SmartPlugHeaderBluetoothSignalIndicator = ({
  client,
  device,
  showSignalLabel,
  showExpand,
  onDragZoom
}) => {

  const isDesktopOrLaptop = useMediaQuery({
    query: '(min-width: 1124px)'
  });

  const hasData = device && device.getSignalsList().length > 0;
  if (!hasData) {
    return (
        <div className="device-wifi-indicator-container"/>
    )
  }

  const strength =
      DBMToSignalStrength(device.getBestSignalStrength() + signalStrengthOffset)
      || SignalStrength.DISCONNECTED

  const shouldShowExpand = isDesktopOrLaptop && showExpand;

  const sortedSignals = device.getSignalsList().slice(0).sort(
        (a, b) => b.getLastUpdated() - a.getLastUpdated());
  const lastUpdatedMillis = sortedSignals[0].getLastUpdated();

  // Assemble signal alt title text
  const now = Date.now();
  var signalTitles = [];
  const formatStr = sortedSignals.some((s) => now-s.getLastUpdated() > 1000*3600) ? 'MMM Do h:mm a' : 'h:mm a';
  sortedSignals.forEach((s) => {
    signalTitles.push(moment(s.getLastUpdated()).format(formatStr) + " - " + s.getClientHostname() + ": " + s.getSignalStrength() + " dBm");
  });
  const signalTitle = signalTitles.join("\n");

  return (
      <div className="device-wifi-indicator-container">
          {showSignalLabel && <div className="device-wifi-indicator-label"
               title={signalTitle}>Signal: {strength}</div>}
          <WifiIndicator title={strength} strength={strength} style={{
            "margin": "0 5px 0 5px",
            "filter": "hue-rotate(20deg)",
            "height": 16,
            "float": "right",
          }}/>
          <div title={signalTitle} className="device-wifi-indicator-time">
            {moment(lastUpdatedMillis).format('h:mm a')}
          </div>
          {shouldShowExpand && <ExpandedSmartPlugChartDialog key={device.getId()} client={client} device={device} onDragZoom={onDragZoom}/>}
      </div>
  )
}

const SmartPlugHeader = ({client, device, data, metadata, showSignalLabel, showExpand, onDragZoom, onLegendClick}) => {
  const hasData = data.length > 0;
  const lastItem = (hasData && data[data.length - 1]) || {};
  let avgDollarsPerMonth = 0.0;
  if (metadata && metadata.get && metadata.get("centiwatts")) {
    const avgDollarsPerDay = metadata.get("centiwatts").getSeriesAvg() / 100 * 24 / 1000 * 0.21;
    avgDollarsPerMonth = Number(avgDollarsPerDay).toFixed(2) * 30;
  }

  return (
      <div className="device-header">
        <SmartPlugHeaderLabels client={client} hasData={hasData} lastItem={lastItem} avgDollarsPerMonth={avgDollarsPerMonth} device={device} onLegendClick={onLegendClick}/>
        <SmartPlugHeaderBluetoothSignalIndicator client={client} device={device} showSignalLabel={showSignalLabel} showExpand={showExpand} onDragZoom={onDragZoom}/>
      </div>
  );

};

export default SmartPlugHeader
